import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button, CircularProgress, Divider, IconButton, Stack, Typography} from '@mui/material';
import {Outlet, useNavigate, useParams} from 'react-router-dom';
import {Alert, ArrowBack, CheckCircleGreen, CheckCircleGrey, Cross, Delete} from '@icons/index';
import {useTranslation} from 'react-i18next';
import {Colors} from '@constants/colors.constants';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import SearchComponent from '@components/inputs/search/Search.component';
import QRCode from 'react-qr-code';
import {
  PurchaseStatementDetails,
  purchaseStatementsQuery,
  purchaseStatementsService,
  searchPurchaseStatementsEffect
} from '@store/invoices/purchaseStatements';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {NumberExt} from '@utils/extensions/number.extension';
import {useSnackbar} from 'notistack';
import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';
import {ExecutionLine, executionLineService, ExecutionLineTotalDto} from '@store/invoices/executionLines';
import {finalize, forkJoin, map, of, switchMap, tap} from 'rxjs';
import {AdminRoutes, InvoicesRoutes} from '@utils/routes.utils';
import dayjs from 'dayjs';
import {Block, CancelOutlined, Launch, PictureAsPdfOutlined} from '@mui/icons-material';
import NewTableComponent from '@components/table/NewTable.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridValueFormatterParams, GridValueGetterParams, GridValueSetterParams,} from '@mui/x-data-grid-pro';
import {ServiceKind, serviceKindsService} from '@store/settings/serviceKinds';
import {OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import SelectComponent, {SelectItem} from '@components/inputs/select/Select.component';
import {InvoiceClient, InvoiceStatus} from '@store/invoices/invoices';
import DeleteBillModal from '@screens/admin/invoices/bills/details/DeleteBill.modal';
import {useObservable} from '@ngneat/react-rxjs';
import {useEffectFn} from '@ngneat/effects-hooks';
import {readyForInvoicingKey} from '@screens/admin/invoices/executions/Executions.tab';
import {History, historyService, HistoryType} from '@store/settings/history';
import {PaymentTerm, TaxRule} from '@store/common/organisation.enums';
import CancelPurchaseStatementModal
  from '@screens/admin/invoices/purchaseStatement/cancel/CancelPurchaseStatement.modal';
import {wasteFamiliesService} from '@store/settings/wasteFamilies';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import ExpandableIconButtonComponent from '@components/buttons/ExpandableIconButton.component';


interface PurchaseStatementDetailsScreenProps {
  isCreation?: boolean;
}

const PurchaseStatementDetailsScreen = (props: PurchaseStatementDetailsScreenProps) => {
  const {isCreation} = props;

  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {statementId} = useParams();
  const searchPurchaseStatements = useEffectFn(searchPurchaseStatementsEffect);

  const [{purchaseStatements}] = useObservable(purchaseStatementsQuery.purchaseStatements$);

  const [filters] = useObservable(purchaseStatementsQuery.filters$);
  const [sortModel] = useObservable(purchaseStatementsQuery.sort$);
  const [tablePagination] = useObservable(purchaseStatementsQuery.pagination$);

  const handleClose = () => navigate('..', {replace: true});

  const [loading, setLoading] = useState(false);
  const [billLoading, setBillLoading] = useState(false);
  const [supplierDocumentNumber, setSupplierDocumentNumber] = useState(false);
  const [history, setHistory] = useState<History[] | undefined>();
  const [bypassQR, setBypassQR] = useState(false);

  const [invoiceLineToAdd, setInvoiceLineToAdd] = useState<Partial<ExecutionLine> | false>(false);

  const [purchaseDetails, setPurchaseDetails] = useState<Partial<PurchaseStatementDetails> | undefined>();
  const [selectedInvoiceLines, setSelectedInvoiceLines] = useState<number[]>([]);

  const [showDeletePS, setShowDeletePS] = useState<boolean>(false);
  const [showCancelBill, setShowCancelBill] = useState<boolean>(false);
  const [execLinesOnError, setExecLinesOnError] = useState<number[] | undefined>(undefined);

  const searchedExecutionLines = useMemo(() => purchaseDetails?.supplierInvoiceLines ?? [], [purchaseDetails?.supplierInvoiceLines]);


  const getFuturePurchaseDetails = () => {
    const ids = JSON.parse(localStorage.getItem(readyForInvoicingKey) || '[]').map(el => el.id);
    if (ids.length > 0) {
      return forkJoin([
        purchaseStatementsService.getExecutionsLinesForIds(ids),
        executionLineService.getExecutionLinesTotal(ids),
      ]);
    }
    return of([[], {} as any]);
  };

  const updateExecutionLines = (supplierInvoiceLines: ExecutionLine[], total: ExecutionLineTotalDto) => {
    const clients: InvoiceClient[] = supplierInvoiceLines.flatMap((e) => [e.clientOrganisationUnit]);

    setPurchaseDetails((state) => ({
      ...state,
      supplierInvoiceLines: supplierInvoiceLines,
      totalAmountVatIncludedInCents: total.totalCostVatIncluded,
      totalAmountNoVatInCents: total.totalCost,
      clients: clients.filter((c, index) => index === clients.findIndex((oc) => oc.id === c.id)),
      paymentTermsNumDays: total.paymentTerm,
      taxRule: total.taxRule,
    }));
  };

  const updatePurchaseStatement = (name: string) => (value: any) => setPurchaseDetails((state) => ({
    ...state,
    [name]: value
  }));

  const handleUpdateStatusPurchaseStatement = (status?: InvoiceStatus, comment?: string) => {
    if (statementId) {
      purchaseStatementsService.updatePurchaseStatement(statementId, status, comment).subscribe({
        next: (po) => {
          setPurchaseDetails(po);
          enqueueSnackbar(t('invoices.purchaseStatement.update.success'), {variant: 'success'});
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  useEffect(() => {
    searchPurchaseStatements({filters, tablePagination, sort: sortModel});
  }, [filters, searchPurchaseStatements, sortModel, tablePagination]);

  useEffect(() => {
    if (purchaseStatements && purchaseDetails) {
      setSupplierDocumentNumber(false);
    }
  }, [purchaseDetails, purchaseStatements]);

  useEffect(() => {
    if (!isCreation && statementId) {
      setBillLoading(true);
      purchaseStatementsService.getPurchaseStatementsDetails(statementId).subscribe({
        next: setPurchaseDetails,
        error: (err) => enqueueSnackbar(err.text, err.options),
        complete: () => setBillLoading(false),
      });
      historyService.getHistoryByResourceId(statementId, 'finance.supplierinvoice').subscribe({
        next: (res) => setHistory(res.sort((h1, h2) => new Date(h2.createdAt).getTime() - new Date(h1.createdAt).getTime())),
        complete: () => setLoading(false),
      });
    }
  }, [enqueueSnackbar, isCreation, statementId]);

  useEffect(() => {
    if (isCreation) {
      getFuturePurchaseDetails().subscribe({
        next: ([el, total]) => updateExecutionLines(el, total),
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, isCreation, statementId]);

  if (!purchaseDetails) return null;

  const orphanLines = (purchaseDetails.supplierInvoiceLines || []).filter((i) => i.isOrphan && !i.isNew);
  const orphanLinesTotalAmountNoVatInCents = orphanLines.reduce((res, line) => res + line.totalBuy ?? 0, 0);
  const orphanLinesTotalAmountVatIncludedInCents = orphanLines.reduce((res, line) => {
    const totalBuy = line.totalBuy ?? 0;
    return res + totalBuy + (totalBuy * 8.1 / 100);
  }, 0);

  const totalAmountNoVatInCents = (purchaseDetails.totalAmountNoVatInCents ?? 0) + orphanLinesTotalAmountNoVatInCents;
  const totalAmountVatIncludedInCents = (purchaseDetails.totalAmountVatIncludedInCents ?? 0) + orphanLinesTotalAmountVatIncludedInCents;
  const invoiceAmount = purchaseDetails.invoiceAmount ?? 0;

  const handleDownloadPdf = () => {
    if (statementId) {
      purchaseStatementsService.printInvoice(statementId).subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  const handleSave = () => {
    if (!supplierDocumentNumber) {
      if (Math.abs(totalAmountVatIncludedInCents - invoiceAmount) > 50) {
        enqueueSnackbar(t('errors.invoicesAmountsDiffCannotBeMoreThan50cents'), {variant: 'error'});
        return;
      }
      setLoading(true);
      purchaseStatementsService.postPurchaseStatement({
        invoiceDate: purchaseDetails.invoiceDate,
        invoiceReceivedAt: purchaseDetails.invoiceReceivedAt,
        invoiceNumber: purchaseDetails.invoiceNumber,
        qrCodeData: purchaseDetails.qrCodeData,
        invoiceAmount: purchaseDetails.invoiceAmount,
        executionLineIds: (purchaseDetails.supplierInvoiceLines || []).filter((i) => !i.isOrphan).map((i) => i.id),
        supplierInvoiceLines: (purchaseDetails.supplierInvoiceLines || []).filter((i) => i.isOrphan).map((i) => ({
          name: i.name,
          kind: i.serviceKind?.id,
          executedAt: i.executedAt,
          puBuy: i.puBuy,
          puSell: i.puBuy,
          quantity: i.quantity,
          waste: i.wasteId,
        })),
      }).pipe(finalize(() => setLoading(false))).subscribe({
        next: (details: PurchaseStatementDetails) => {
          executionLineService.clearExecutionLinesReadyForInvoicing();
          enqueueSnackbar(t('invoices.purchaseStatement.create.success'), {variant: 'success'});
          navigate(`/${AdminRoutes.INVOICES}/${InvoicesRoutes.PURCHASE_STATEMENT}/${details.id}`);
        },
        error: (err) => {
          if (err.data?.executionLineIds) {
            setExecLinesOnError(err.data.executionLineIds);
            enqueueSnackbar(t('invoices.purchaseStatement.create.linesAlreadyInStatement'), {variant: 'error'});
          } else {
            enqueueSnackbar(err.text, err.options);
          }

        }
      });
    } else {
      enqueueSnackbar(t('invoices.purchaseStatement.create.error'), {variant: 'error'});
    }
  };

  const handleDeletePS = () => {
    if (statementId) {
      purchaseStatementsService.deleteStatement(statementId).subscribe({
        next: () => navigate('..'),
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  const handleCreationMode = (executionLine: ExecutionLine & {
    'execution.executedAt': Date;
    'serviceKind.kind': SelectItem;
    waste: SelectItem;
  }) => {
    if (executionLine.isNew) {
      const newOrphanLine = {
        ...executionLine,
        name: executionLine.name,
        kind: executionLine['serviceKind.kind'].data?.kind,
        kindId: executionLine['serviceKind.kind'].value,
        executedAt: executionLine['execution.executedAt'],
        puBuy: executionLine.puBuy,
        puSell: executionLine.puBuy,
        quantity: executionLine.quantity,
        unit: executionLine['serviceKind.kind'].data?.execUnitName1,
        totalBuy: executionLine.puBuy * executionLine.quantity,
        serviceKind: executionLine['serviceKind.kind'].data as ServiceKind,
        waste: executionLine.waste?.data?.name,
        wasteId: executionLine.waste?.value,
      };

      const supplierInvoiceLines = (purchaseDetails.supplierInvoiceLines || []).concat({
        ...newOrphanLine,
        isNew: false
      });
      setPurchaseDetails((state) => ({...state, supplierInvoiceLines}));
      setInvoiceLineToAdd(false);
      return {...executionLine, ...newOrphanLine, kind: executionLine['serviceKind.kind']};
    } else if (executionLine.isOrphan) {
      const updatedExecutionLine = {
        puBuy: executionLine.puBuy,
        quantity: executionLine.quantity,
        totalBuy: executionLine.puBuy * executionLine.quantity,
        name: executionLine.name,
      };

      const supplierInvoiceLines = (purchaseDetails.supplierInvoiceLines || []).map((e) => e.id === executionLine.id ? ({
        ...e,
        ...executionLine,
        ...updatedExecutionLine,
      }) : e);
      setPurchaseDetails((state) => ({...state, supplierInvoiceLines}));
      return {...executionLine, ...updatedExecutionLine};
    } else {
      const updatedExecutionLine = {
        puBuy: executionLine.puBuy,
        quantity: executionLine.quantity,
        totalBuy: executionLine.puBuy * executionLine.quantity,
        name: executionLine.name,
      };

      executionLineService.patchExecutionLine(executionLine.id, updatedExecutionLine)
        .pipe(switchMap(() => getFuturePurchaseDetails())).subscribe({
        next: ([el, total]) => updateExecutionLines(el, total),
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
      return executionLine;
    }
  };

  const handleRemoveExecutionLine = (revertLines: boolean) => {
    if (isCreation) {
      const notDeletedOrphanLines = searchedExecutionLines.filter((e) => e.isOrphan && !selectedInvoiceLines.includes(e.id));
      const selectedOthers = selectedInvoiceLines.filter((iId) => !searchedExecutionLines.find((e) => e.id === iId && e.isOrphan));

      if (selectedOthers.length) {
        executionLineService.removeExecutionLinesFromReadyForInvoicing(selectedOthers);
        getFuturePurchaseDetails().pipe(tap(([el, total]) => updateExecutionLines(el.concat(notDeletedOrphanLines), total))).subscribe();
      } else {
        setPurchaseDetails((state) => ({
          ...state,
          supplierInvoiceLines: (state?.supplierInvoiceLines ?? []).filter((e) => !selectedInvoiceLines.includes(e.id)),
        }));
      }
    } else {
      if (!statementId) return;
      purchaseStatementsService.removePurchaseStatementInvoiceLines(statementId, selectedInvoiceLines, revertLines)
        .pipe(switchMap(() =>
          purchaseStatementsService.getPurchaseStatementsDetails(statementId)
            .pipe(tap(setPurchaseDetails))
        ))
        .subscribe({
          next: () => enqueueSnackbar(t('delete.success.purchaseStatementExecLine'), {variant: 'success'}),
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  };

  const handleResetAccountingTransferDate = () => {
    if (statementId) {
      purchaseStatementsService.resetAccountingTransferDate(statementId).subscribe({
        next: (po) => {
          setPurchaseDetails(po);
          enqueueSnackbar(t('invoices.purchaseStatement.update.success'), {variant: 'success'});
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  const errors = {
    invoiceReceivedAt: getFieldError(purchaseDetails.invoiceReceivedAt || purchaseDetails.dueDate, FieldValidationType.REQUIRED_DATE),
    invoiceAmount: getFieldError(purchaseDetails.invoiceAmount, FieldValidationType.REQUIRED_NUMBER),
    invoiceNumber: getFieldError(purchaseDetails.invoiceNumber, FieldValidationType.REQUIRED_STRING),
    invoiceQR: purchaseDetails.paymentTermsNumDays && PaymentTerm.isQr(purchaseDetails.paymentTermsNumDays) && !bypassQR ? getFieldError(purchaseDetails.qrCodeData, FieldValidationType.REQUIRED_QR) : undefined,
  };

  const delta = NumberExt.round(NumberExt.round(invoiceAmount
    / 100, 2) - NumberExt.round(totalAmountVatIncludedInCents / 100, 2), 2);

  if (billLoading) {
    return <Stack height="100%" justifyContent="center" alignItems="center"><CircularProgress size={50}/></Stack>;
  }

  return (
    <>
      <Stack pt="34px" px="24px" pb="20px" className="scrollable">
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" ml="-9px" mt="-20px">
            <IconButton onClick={handleClose}>
              <ArrowBack width={12} height={12}/>
            </IconButton>
            <Typography fontSize="18px" fontWeight="700">
              {t(`invoices.purchaseStatement.details.${isCreation ? 'newTitle' : 'title'}`)}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" pt="20px" justifyContent="space-between">
          <Stack spacing={1} width="fit-content">
            <Stack direction="row" alignItems="center" pr="127px" spacing={1}>
              <Typography fontSize="20px" fontWeight="700">
                {t(purchaseDetails?.type === 'CANCEL' ? 'invoices.purchaseStatement.details.cancelNumberStatement' : `invoices.purchaseStatement.details.${isCreation ? 'new' : 'numberStatement'}`, {number: purchaseDetails?.id})}
              </Typography>
              {!isCreation && (
                <Typography fontSize="18px" fontWeight="700" color={Colors.textWithOpacity}>
                  {`(${purchaseDetails?.organisationAndUnitName})`}
                </Typography>
              )}
            </Stack>
            <Divider
              orientation="horizontal"
              variant="fullWidth"
              style={{borderWidth: '2px', borderColor: Colors.primary, maxWidth: '100%'}}/>
          </Stack>
          <Stack direction="row" spacing={2}>
            <ExpandableIconButtonComponent
              variant="outlined"
              color="primary"
              onClick={handleDownloadPdf}
              startIcon={<PictureAsPdfOutlined style={{color: '#172134', transform: 'scale(1.1)'}}/>}
              title={t('invoices.invoicesValidation.details.download')}
            />
            {!isCreation && purchaseDetails?.type === 'DEFAULT' && (
              <ExpandableIconButtonComponent
                variant="outlined"
                color="primary"
                onClick={() => setShowCancelBill(true)}
                startIcon={<Block/>}
                title={t('invoices.bills.details.cancel')}/>
            )}
            {isCreation && <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={checkIfErrors(errors) || loading}
            >
              {t('global.save')}
            </Button>
            }
            {
              purchaseDetails?.type === 'CANCEL' && purchaseDetails.invoiceOriginId && (
                <ExpandableIconButtonComponent
                  variant="outlined"
                  color="primary"
                  startIcon={<Launch/>}
                  onClick={() => navigate(`../${purchaseDetails?.invoiceOriginId}`)}
                  title={t('invoices.purchaseStatement.details.seeOriginalBill')}/>
              )
            }
          </Stack>
        </Stack>
      </Stack>
      <Stack overflow="auto" className="scrollable" pb="25px" px="24px" flex={1} spacing={3}>
        <Stack mt="12px" p="18px" bgcolor={Colors.white} direction="row">
          <Stack spacing={3}>
            <Stack direction="row" spacing={2}>
              <Stack spacing={0.5} flex={1}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.purchaseStatement.details.denomination')}
                </Typography>
                <TextFieldComponent
                  readOnly={!isCreation}
                  disabled={isCreation}
                  placeholder={isCreation ? t('invoices.purchaseStatement.details.denominationPlaceholder') : undefined}
                  value={purchaseDetails?.invoiceRef ?? ''}/>
              </Stack>
              {!isCreation && (
                <Stack spacing={0.5} flex={1}>
                  <Typography fontSize="16px" fontWeight="400">
                    {t('invoices.purchaseStatement.details.accountantTransferDate')}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Box flex={1}>
                      <TextFieldComponent
                        readOnly={true}
                        value={purchaseDetails?.accountingTransferDate ? dayjs(purchaseDetails?.accountingTransferDate).format('DD/MM/YYYY HH:mm') : ''}
                      />
                    </Box>

                    <Button variant="outlined" color="primary" onClick={handleResetAccountingTransferDate}>
                      {t('invoices.purchaseStatement.details.removeTransferDate')}
                    </Button>

                  </Stack>

                </Stack>
              )}
            </Stack>

            <Stack direction="row" spacing={2}>
              {!isCreation && <Stack flex={1} spacing={0.5}>
                  <Typography fontSize="16px" fontWeight="400">
                    {t('invoices.purchaseStatement.details.dateCharge')}*
                  </Typography>
                  <DatePickerComponent
                      color="secondary"
                      readOnly={!isCreation}
                      value={purchaseDetails?.invoiceDate}
                  />
              </Stack>}
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.purchaseStatement.details.dateDeparture')}*
                </Typography>
                <DatePickerComponent
                  color="secondary"
                  readOnly={!isCreation}
                  handleChange={updatePurchaseStatement('invoiceReceivedAt')}
                  value={purchaseDetails?.reminderDate || purchaseDetails?.invoiceReceivedAt}
                  error={errors.invoiceReceivedAt}
                />
              </Stack>
              {!isCreation && <Stack flex={1} spacing={0.5}>
                  <Typography fontSize="16px" fontWeight="400">
                    {t('invoices.purchaseStatement.details.dueDate')}*
                  </Typography>
                  <DatePickerComponent
                      color="secondary"
                      readOnly={true}
                      value={purchaseDetails?.dueDate}
                  />
              </Stack>}
            </Stack>
            <Stack direction="row" spacing={2} position="relative">
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.purchaseStatement.details.HTAmount')}
                </Typography>
                <NumberTextFieldComponent
                  readOnly
                  endAdornment={
                    <Typography fontSize={14} fontWeight="400">
                      {purchaseDetails.currency}
                    </Typography>
                  }
                  value={NumberExt.round(totalAmountNoVatInCents / 100, 2)}
                />
              </Stack>
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.purchaseStatement.details.TTCAmount')}
                </Typography>
                <NumberTextFieldComponent
                  readOnly
                  endAdornment={
                    <Typography fontSize={14} fontWeight="400">
                      {purchaseDetails.currency}
                    </Typography>
                  }
                  value={NumberExt.round(totalAmountVatIncludedInCents / 100, 2)}
                />
              </Stack>
              <Stack flex={1} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.purchaseStatement.details.NETAmount')}*
                </Typography>
                {!isCreation && <NumberTextFieldComponent
                    readOnly
                    endAdornment={(
                      <Typography fontSize={14} fontWeight="400">
                        {purchaseDetails.currency}
                      </Typography>
                    )}
                    value={NumberExt.round((purchaseDetails.invoiceAmount || 0) / 100, 2)}
                />}
                {isCreation && <NumberTextFieldComponent
                    handleChange={(invoiceAmount) => updatePurchaseStatement('invoiceAmount')((invoiceAmount ?? 0) * 100)}
                    value={purchaseDetails.invoiceAmount ? purchaseDetails?.invoiceAmount / 100 : undefined}
                    error={errors.invoiceAmount}
                />}
              </Stack>
              <Stack spacing={0.5} position="absolute" pl="15px" right="0" width="100%"
                     sx={{transform: 'translateX(100%)'}}>
                <Box height="20px"/>
                <Stack justifyContent="center" height="40px">
                  <Typography fontSize="14px" fontWeight="400" color={Colors.textWithOpacity}>
                    {delta < 0 ? '' : '+'}
                    {delta} {purchaseDetails.currency}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            {!isCreation && <Stack direction="row" spacing={2}>
                <Stack flex={1} spacing={0.5}>
                    <Typography fontSize="16px" fontWeight="400">
                      {t('invoices.purchaseStatement.details.changeRate')}
                    </Typography>
                    <NumberTextFieldComponent
                        readOnly
                        value={purchaseDetails.changeRate}
                    />
                </Stack>
                <Stack flex={1} spacing={0.5}>
                    <Typography fontSize="16px" fontWeight="400">
                      {t('invoices.purchaseStatement.details.totalAmountCHF')}
                    </Typography>
                    <NumberTextFieldComponent
                        readOnly
                        endAdornment={
                          <Typography fontSize={14} fontWeight="400">
                            CHF
                          </Typography>
                        }
                        value={NumberExt.round((purchaseDetails?.totalAmountVatIncludedInCentsCHF || 0) / 100, 2)}
                    />
                </Stack>
            </Stack>}
            <Stack spacing={0.5}>
              <Typography fontSize="16px" fontWeight="400">
                {t('invoices.purchaseStatement.details.comment')}
              </Typography>
              <TextFieldComponent
                handleKeyPress={(evt) => () => {
                  if (((evt.target as any).value?.length ?? 0) >= 150) evt.preventDefault();
                }}
                value={purchaseDetails.comment}
                handleChange={(value) => {
                  if (purchaseDetails) {
                    setPurchaseDetails({...purchaseDetails, comment: value});
                  }
                }}
                handleBlur={(comment) => handleUpdateStatusPurchaseStatement(undefined, comment)}
              />
            </Stack>
          </Stack>
          <Stack spacing={3} pl="195px" flex={1}>
            <Stack direction="row" spacing={1}>
              <Stack flex={0.5} spacing={0.5}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.purchaseStatement.details.billNumber')}*
                </Typography>
                <TextFieldComponent
                  readOnly={!isCreation}
                  handleChange={updatePurchaseStatement('invoiceNumber')}
                  value={purchaseDetails?.invoiceNumber ?? ''}
                  error={errors.invoiceNumber}/>
              </Stack>
              {!isCreation && <Stack flex={0.5} spacing={0.5}>
                  <Typography fontSize="16px" fontWeight="400">
                    {t('invoices.bills.details.status')}*
                  </Typography>
                  <SelectComponent
                      readOnly={false}
                      items={
                        InvoiceStatus.selectItems().filter(i => i.value != InvoiceStatus.DRAFT)
                      }
                      handleChange={(value) => handleUpdateStatusPurchaseStatement(value)}
                      value={purchaseDetails?.status ?? ''}
                  />
              </Stack>}
            </Stack>
            <Stack spacing={2} direction={'row'}>
              <Stack spacing={0.5} flex={1}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.purchaseStatement.details.taxRule')}*
                </Typography>
                <TextFieldComponent
                  readOnly
                  value={TaxRule.selectItems().find((t) => t.value === purchaseDetails?.taxRule)?.label ?? ''}
                />
              </Stack>
              <Stack spacing={0.5} flex={1}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.purchaseStatement.details.paymentTermNumDays')}*
                </Typography>
                <TextFieldComponent
                  readOnly={true}
                  value={PaymentTerm.selectItems().find((t) => t.value === purchaseDetails?.paymentTermsNumDays)?.label ?? 'Auto.'}
                />
              </Stack>
            </Stack>
            <Stack sx={{position: 'relative'}} spacing={0.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.purchaseStatement.details.reference')}*
                </Typography>
                <CheckboxComponent
                  value={bypassQR}
                  handleChange={(value) => setBypassQR(value)}
                  label={t('invoices.purchaseStatement.details.bypassQR')}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Stack flex={1}>
                  <TextFieldComponent multiline rows={7} handleChange={(value) => {
                    const amount = parseFloat(value.split('\n')?.[18] ?? 0);
                    updatePurchaseStatement('qrCodeData')(value);
                    updatePurchaseStatement('invoiceAmount')(amount * 100);
                  }} readOnly={!isCreation} value={purchaseDetails.qrCodeData} error={errors.invoiceQR}/>
                </Stack>
                {!isCreation && purchaseDetails?.qrCodeData && purchaseDetails?.qrCodeData.length > 0 &&
                    <QRCode style={{height: '70%'}} value={purchaseDetails?.qrCodeData}/>}
              </Stack>
              {purchaseDetails?.type === 'CANCEL' && <Stack spacing={0.5}>
                  <Typography fontSize="16px" fontWeight="400">
                    {t('invoices.purchaseStatement.details.cancellationReason')}
                  </Typography>
                  <TextFieldComponent
                      readOnly
                      value={purchaseDetails.cancellationReason}/>
              </Stack>}
            </Stack>
          </Stack>
        </Stack>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            {/*{!isCreation && (*/}
            {/*  <Button*/}
            {/*    variant="outlined"*/}
            {/*    color="primary"*/}
            {/*    disabled={!selectedInvoiceLines.length}*/}
            {/*    onClick={() => handleRemoveExecutionLine(true)}*/}
            {/*    startIcon={<Replay style={{ height: 22, width: 22 }} />}>*/}
            {/*    {t('invoices.purchaseStatement.details.putInBacklog')}*/}
            {/*  </Button>*/}
            {/*)}*/}
            {isCreation && <Button
                variant="outlined"
                color="primary"
                disabled={!selectedInvoiceLines.length}
                onClick={() => handleRemoveExecutionLine(false)}
                startIcon={<Delete/>}>
              {t('invoices.purchaseStatement.details.delete')}
            </Button>}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Box width="500px">
              <SearchComponent/>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Cross/>}
                size="small">
                {t('global.resetFilters')}
              </Button>
              <Divider variant="fullWidth" orientation="vertical" sx={{height: '30px'}}/>
              <Button
                variant="outlined"
                color="primary"
                size="small">
                {t('global.filters')}
              </Button>
            </Stack>
          </Stack>
          <Stack minHeight={500} pb="20px">
            <NewTableComponent
              columns={[
                COLUMN_TYPES.ICON({
                  field: 'execution.isCancelled',
                  color: 'secondary',
                  label: '',
                  minWidth: 30,
                  width: 30,
                  renderIcon: (executionLine: ExecutionLine) => executionLine.cancelledByLineId ?
                    <CancelOutlined fontSize="small"/> : <></>,
                }),
                COLUMN_TYPES.ICON({
                  field: 'error',
                  renderIcon: (execLine) => execLinesOnError?.includes(execLine.id) ?
                    <Alert style={{height: 18, width: 21}}/> : null,
                  width: 40,
                }),
                COLUMN_TYPES.DATE({
                  field: 'execution.executedAt',
                  cellClassName: () => 'secondary',
                  hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                  label: t('invoices.executions.table.executedAt'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.executedAt,
                  valueFormatter: (params: GridValueFormatterParams) => params.value ? t('global.date', {date: new Date(params.value)}) : '',
                  width: 200,
                }),
                isCreation ? COLUMN_TYPES.INPUT({
                  field: 'name',
                  color: 'secondary',
                  label: t('invoices.executions.table.name'),
                  fullWidth: true,
                  minWidth: 250,
                }) : COLUMN_TYPES.TEXT({
                  field: 'name',
                  color: 'secondary',
                  label: t('invoices.executions.table.name'),
                  fullWidth: true,
                  minWidth: 250,
                }),
                isCreation ? COLUMN_TYPES.ASYNC_SELECT({
                  field: 'serviceKind.kind',
                  cellClassName: () => 'bold',
                  hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                  label: t('invoices.executions.table.type'),
                  getOptions: (search) => serviceKindsService.searchServiceKinds(search).pipe(
                    map((options) =>
                      options.data.map((o) => ({label: o.kind || '-', value: o.id, data: o}))
                    )
                  ),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.kind,
                  width: 250,
                }) : COLUMN_TYPES.TEXT({
                  field: 'serviceKind.kind',
                  cellClassName: () => 'bold',
                  hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                  label: t('invoices.executions.table.type'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.kind,
                  width: 250,
                }),
                isCreation ? COLUMN_TYPES.ASYNC_SELECT({
                  field: 'waste',
                  hideInput: (executionLine: ExecutionLine) => !executionLine.isNew,
                  label: t('invoices.executions.table.waste'),
                  getOptions: (search) => wasteFamiliesService.searchWaste(search).pipe(
                    map((options) => options.data.map((w) => ({label: w.name || '-', value: w.id, data: w})))),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.waste,
                  width: 250,
                }) : COLUMN_TYPES.TEXT({
                  field: 'waste',
                  color: 'secondary',
                  label: t('invoices.executions.table.waste'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.waste,
                  width: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'container',
                  color: 'secondary',
                  label: t('invoices.executions.table.container'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.container,
                  width: 200,
                }),
                isCreation ? COLUMN_TYPES.NUMBER({
                  field: 'executionLine.quantity',
                  label: t('invoices.executions.table.quantity'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.quantity,
                  valueSetter: (params: GridValueSetterParams) => ({
                    ...params.row,
                    quantity: params.value ?? 0,
                    totalBuy: (params.value ?? 0) * (params.row.puBuy ?? 0) * 100,
                  }),
                  width: 100,
                }) : COLUMN_TYPES.TEXT({
                  field: 'executionLine.quantity',
                  label: t('invoices.executions.table.quantity'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.quantity,
                  width: 100,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'serviceKind.execUnitName1',
                  color: 'secondary',
                  label: t('invoices.executions.table.unit'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.unit,
                  width: 100,
                }),
                isCreation ? COLUMN_TYPES.NUMBER({
                  field: 'executionLine.puBuy',
                  label: t('invoices.executions.table.puBuy'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                    params.row.puBuy ? NumberExt.round(params.row.puBuy / 100, 2) : undefined,
                  valueSetter: (params: GridValueSetterParams) => ({
                    ...params.row,
                    puBuy: (params.value ?? 0) * 100,
                    totalBuy: (params.value ?? 0) * (params.row.quantity ?? 0) * 100,
                  }),
                  width: 100,
                }) : COLUMN_TYPES.TEXT({
                  field: 'executionLine.puBuy',
                  label: t('invoices.executions.table.puBuy'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                    params.row.puBuy ? NumberExt.round(params.row.puBuy / 100, 2) : undefined,
                  width: 100,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'totalBuy',
                  label: t('invoices.executions.table.totalBuy'),
                  valueFormatter: (params: GridValueFormatterParams) =>
                    params.value ? NumberExt.round(params.value / 100, 2) : '',
                  width: 100,
                }),
                COLUMN_TYPES.ICON({
                  field: 'clientInvoiceLineId',
                  label: t('invoices.executions.table.billsShort'),
                  renderIcon: (executionLine: ExecutionLine) => {
                    if (!executionLine.clientInvoicingRequired) return <Box>-</Box>;
                    return executionLine.clientInvoiceLineId ? <CheckCircleGreen/> : <CheckCircleGrey/>;
                  },
                  width: 40,
                }),
                COLUMN_TYPES.ACTIONS({
                  handleShowRow: (executionLine: ExecutionLine) =>
                    executionLine.executionId ? navigate(`${executionLine.executionId}`) : undefined,
                  disabledShowRow: (executionLine: ExecutionLine) => !executionLine.executionId,
                  handleCancel: () => setInvoiceLineToAdd(false),
                  otherActions: isCreation ? [{type: OtherActionType.EDIT}] : [],
                }),
              ]}
              paginationMode="client"
              sortingMode="client"
              disablePagination
              disableRowSelection={!isCreation}
              disableEdition={!isCreation}
              handleRowSelected={setSelectedInvoiceLines}
              rowsSelected={selectedInvoiceLines}
              autoHeight={false}
              sortModel={[
                {field: 'execution.executedAt', sort: 'asc'},
                {field: 'waste', sort: 'asc'},
                {field: 'container', sort: 'asc'},
              ]}
              handleRowChanged={isCreation ? (executionLine: ExecutionLine & {
                'execution.executedAt': Date;
                'serviceKind.kind': SelectItem;
                waste: SelectItem;
              }) => {
                return handleCreationMode(executionLine);
              } : undefined}
              data={((invoiceLineToAdd ? [invoiceLineToAdd as ExecutionLine] : [])).concat(searchedExecutionLines)}/>
          </Stack>
        </Stack>
        <Stack spacing={2} minHeight={300} pb="20px">
          <Typography fontWeight="700" fontSize="18px">
            {t('invoices.purchaseStatement.details.sites.title')}
          </Typography>
          <NewTableComponent
            columns={[
              COLUMN_TYPES.TEXT_BOLD({
                field: 'clientName',
                label: t('invoices.purchaseStatement.details.sites.table.client'),
                valueFormatter: (params) => (params.value ? params.value.toUpperCase() : ''),
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'clientUnitName',
                label: t('invoices.purchaseStatement.details.sites.table.site'),
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'phone',
                label: t('invoices.purchaseStatement.details.sites.table.phone'),
                width: 150,
              }),
              COLUMN_TYPES.TEXT({
                field: 'address',
                cellClassName: () => 'pre-line',
                color: 'secondary',
                label: t('invoices.purchaseStatement.details.sites.table.address'),
                valueFormatter: (params: GridValueFormatterParams) =>
                  `${params.value?.street || '-'}\n${params.value?.zip || '-'} ${params.value?.city || '-'}`,
                fullWidth: true,
                minWidth: 350,
              }),
              COLUMN_TYPES.TEXT({
                field: 'country',
                label: t('invoices.purchaseStatement.details.sites.table.country'),
                valueGetter: (params: GridValueGetterParams) => params.row.address?.country,
                valueFormatter: (params) => (params.value ? params.value.toUpperCase() : ''),
                width: 150,
              }),
            ]}
            paginationMode="client"
            disablePagination
            disableRowSelection
            data={purchaseDetails?.clients ?? []}
          />
        </Stack>
        <Stack spacing={2} minHeight={300}>
          <Typography fontWeight="700" fontSize="18px">
            {t('routes.history')}
          </Typography>
          <NewTableComponent
            loading={loading}
            columns={[
              COLUMN_TYPES.TEXT_BOLD({
                field: 'id',
                label: t('settings.histories.table.id'),
                valueGetter: (params: GridValueGetterParams<any, History>) => `${params.row.id}`,
                fullWidth: true,
                width: 50,
              }),
              COLUMN_TYPES.TEXT({
                field: 'createdAt',
                label: t('settings.histories.table.createdAt'),
                sortable: true,
                valueGetter: (params: GridValueGetterParams<any, History>) => params.row.createdAt,
                valueFormatter: (params) => (params.value ? t('global.date', {date: new Date(params.value)}) : '-'),
                width: 130,
              }),
              COLUMN_TYPES.TEXT({
                field: 'user',
                label: t('settings.histories.table.user'),
                fullWidth: true,
                valueGetter: (params: GridValueGetterParams<any, History>) => {
                  if (!params.row.authorLastName || !params.row.authorFirstName) return ' - ';

                  return `${params.row.authorLastName} ${params.row.authorFirstName}`;
                },
                minWidth: 150,
              }),
              COLUMN_TYPES.TEXT({
                field: 'tableName',
                label: t('settings.histories.table.tableName'),
                fullWidth: true,
                minWidth: 150,
              }),
              COLUMN_TYPES.TEXT({
                field: 'eventType',
                label: t('settings.histories.table.type'),
                valueFormatter: (params) =>
                  HistoryType.selectItems().find((s) => s.value === params.value)?.label || '',
                fullWidth: true,
                minWidth: 120,
              }),
            ]}
            paginationMode="client"
            disablePagination
            disableRowSelection
            data={history}
          />
        </Stack>
      </Stack>
      {showDeletePS && (
        <DeleteBillModal
          handleClose={() => setShowDeletePS(false)}
          handleDelete={() => {
            handleDeletePS();
          }}/>
      )}
      {
        showCancelBill && (
          <CancelPurchaseStatementModal purchaseStatementDetails={purchaseDetails}
                                        handleClose={() => setShowCancelBill(false)}/>
        )
      }
      <Outlet/>
    </>
  );
};

export default PurchaseStatementDetailsScreen;
